// T035 — Statsig implementation of AnalyticsProvider interface.
// Wraps the StatsigClient to conform to the vendor-neutral analytics API.

import { StableID, StatsigClient, type StatsigUpdateDetails } from '@statsig/js-client';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import { featureGates } from '@/config/feature-gates';
import { beginGateValuesUpdate, endGateValuesUpdate } from '@/lib/analytics/gate-values';
import { logger } from '@/lib/telemetry/logger';
import type { AnalyticsConfig, AnalyticsProvider, ExperimentResult } from '../../types';

export class StatsigAnalyticsProvider implements AnalyticsProvider {
  private client: StatsigClient | null = null;
  private sdkKey = '';
  private readonly unavailableGateReasons = new Set<string>();
  private readonly missingTradeTraceEvents = new Set<string>();
  // Counts identity updates so that a superseded one is not logged as a failure.
  private identifyGeneration = 0;
  private stableIdGatesMissingWarned = false;

  async initialize(config: AnalyticsConfig): Promise<void> {
    if (!config.clientKey) {
      return;
    }

    this.sdkKey = config.clientKey;
    this.client = new StatsigClient(
      config.clientKey,
      withStableId(config.clientKey, {
        userID: config.userId ?? '',
        custom: { platform: 'web' },
      }),
      {
        environment: { tier: config.environment },
        // Share the anonymous assignment with server-rendered Smart App Banners.
        enableCookies: true,
      },
    );
    // Statsig reports a failed fetch as an unsuccessful result; it does not
    // reject. An identity update that starts during init supersedes the
    // anonymous fetch on purpose, so that result is not reported.
    const details = await this.client.initializeAsync();
    if (this.identifyGeneration === 0) {
      this.reportSettledUpdate(details, 'Statsig init fetch failed');
    }
    // @ts-expect-error REASON: @statsig types incompatible with exactOptionalPropertyTypes (library bug)
    runStatsigAutoCapture(this.client, {
      consoleLogAutoCaptureSettings: { enabled: true, logLevel: 'error' },
    });
    void startSessionReplayAfterLoad(this.client);
  }

  checkGate(name: string): boolean {
    if (!this.client) {
      return false;
    }
    // Inspect without logging first. Statsig treats an exposure with an
    // unavailable assignment reason as a Group Assignment Health failure,
    // even though the feature was never served.
    const gate = this.client.getFeatureGate(name, { disableExposureLog: true });
    // Return `false` for any state where Statsig hasn't given us a real value:
    // - `<source>:Unrecognized` — no assignment for the gate: it is enabled
    //   with no rules, missing from the dashboard, or (`Loading:`) the values
    //   are still loading. These are ordinary states, so they are not logged.
    // - `NoValues` / `Uninitialized` — no values loaded (e.g. network failure
    //   to featureassets.org / prodregistryv2.org).
    // Unknown gates correctly collapse to `false` for every user. Explicit
    // DevToolkit overrides are applied by `lib/analytics/index.ts:checkGate`.
    const reason = gate.details.reason;
    if (reason.endsWith(':Unrecognized')) {
      return false;
    }
    if (reason === 'NoValues' || reason === 'Uninitialized') {
      const unavailableKey = `${name}:${reason}`;
      if (!this.unavailableGateReasons.has(unavailableKey)) {
        this.unavailableGateReasons.add(unavailableKey);
        logger.warn('Statsig gate evaluation unavailable', { gateName: name, reason });
      }
      return false;
    }
    // Record the exposure only after the assignment is known to be usable.
    return this.client.getFeatureGate(name).value;
  }

  getConfig(name: string): Record<string, unknown> {
    if (!this.client) {
      return {};
    }
    const config = this.client.getDynamicConfig(name);
    return config.value;
  }

  getExperiment(name: string): ExperimentResult {
    if (!this.client) {
      return { value: null, ruleID: '' };
    }
    const experiment = this.client.getExperiment(name);
    return { value: experiment.value, ruleID: experiment.ruleID };
  }

  trackEvent(name: string, properties: Record<string, unknown>): void {
    if (!this.client) {
      return;
    }
    // Statsig logEvent metadata accepts Record<string, string>; convert values.
    const metadata: Record<string, string> = {};
    for (const [key, val] of Object.entries(properties)) {
      metadata[key] = String(val);
    }
    // Event Value is Statsig's per-event funnel unit. Reuse the submission's
    // trace rather than changing the user's identity or counting users.
    this.client.logEvent(name, this.tradeFunnelValue(name, properties), metadata);
  }

  identifyUser(userId: string, attributes: Record<string, unknown>): void {
    if (!this.client) {
      return;
    }
    // Statsig switches identity immediately, loads that user's cached values,
    // and fetches fresh evaluations. Its initiator guard discards responses
    // superseded by a later identify, including a richer same-account identity.
    // Keep this synchronous for auth events emitted immediately after identify;
    // queuing updates behind the network would attribute those events too late.
    // Gate consumers, however, must not commit to another user's evaluation
    // while this user's values are in flight — signal pending → settled.
    this.identifyGeneration += 1;
    const generation = this.identifyGeneration;
    const update = this.client.updateUserAsync(
      withStableId(this.sdkKey, statsigUserFromIdentity(userId, attributes)),
    );
    // The call above has already emptied the store and loaded this user's
    // cache, so the store now shows whether there are values to serve.
    beginGateValuesUpdate(userId, this.client.getContextHandle().values !== null);
    update
      .then((details) => {
        if (generation === this.identifyGeneration) {
          this.reportSettledUpdate(details, 'Statsig identity update failed');
        }
      })
      .finally(endGateValuesUpdate);
  }

  shutdown(): void {
    if (this.client) {
      this.client.shutdown();
      this.client = null;
    }
  }

  // ALLOW-UNKNOWN: analytics provider boundary accepts arbitrary event metadata.
  private tradeFunnelValue(name: string, properties: Record<string, unknown>): string | undefined {
    if (
      ![
        'trade_attempted',
        'trade_success',
        'trade_error',
        'trade_cancelled',
        'trade_unknown',
      ].includes(name)
    )
      return undefined;
    const instruction = properties.instruction;
    if (instruction !== 'buy' && instruction !== 'sell' && instruction !== 'swap') return undefined;
    const traceId = properties.traceId;
    if (typeof traceId === 'string' && traceId.trim() !== '') return traceId;
    if (!this.missingTradeTraceEvents.has(name)) {
      this.missingTradeTraceEvents.add(name);
      logger.warn('analytics.trade_trace_missing', { eventName: name });
    }
    return undefined;
  }

  /**
   * Logs a failed update. After a successful one, warns once per session when
   * the payload has none of the stableID gates: Statsig evaluated without the
   * stableID (as on 2026-09-21), so every stableID gate reads as off.
   */
  private reportSettledUpdate(details: StatsigUpdateDetails, failureMessage: string): void {
    if (!details.success) {
      logger.warn(failureMessage, statsigFailureFields(details));
      return;
    }
    if (this.stableIdGatesMissingWarned || this.client === null) return;
    if (!payloadHasNoStableIdGates(this.client)) return;

    this.stableIdGatesMissingWarned = true;
    logger.warn('Statsig payload has no stableID gates', {
      source: details.source,
      gateCount: STABLE_ID_GATE_NAMES.length,
    });
  }
}

const STATSIG_TOP_LEVEL_KEYS = ['email', 'country', 'locale', 'appVersion'] as const;
type StatsigTopLevelKey = (typeof STATSIG_TOP_LEVEL_KEYS)[number];

function isStatsigTopLevelKey(key: string): key is StatsigTopLevelKey {
  return (STATSIG_TOP_LEVEL_KEYS as readonly string[]).includes(key);
}

/**
 * Split incoming attributes into Statsig's first-class user fields vs. the
 * catch-all `custom` map. First-class fields drive built-in dashboard
 * filters (Country, Email, …). `platform` is always `web` so web vs mobile
 * segmentation cannot be overwritten by a caller attribute.
 */
function statsigUserFromIdentity(
  userId: string,
  attributes: Record<string, unknown>,
): {
  userID: string;
  email?: string;
  country?: string;
  locale?: string;
  appVersion?: string;
  custom: Record<string, string | number | boolean>;
} {
  const topLevel: Partial<Record<StatsigTopLevelKey, string>> = {};
  const custom: Record<string, string | number | boolean> = {};
  for (const [key, val] of Object.entries(attributes)) {
    if (val === null || val === undefined) continue;
    if (isStatsigTopLevelKey(key) && typeof val === 'string') {
      topLevel[key] = val;
      continue;
    }
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      custom[key] = val;
    } else {
      custom[key] = String(val);
    }
  }
  custom.platform = 'web';
  return { userID: userId, ...topLevel, custom };
}

/**
 * Statsig evaluates a stableID-typed gate only when the user object itself
 * carries `customIDs.stableID`. The SDK generates and persists the id but
 * never attaches it to the user, so without this every stableID gate
 * (`web_region`, `web_rewards_platform`, …) came back
 * `Unrecognized` and read as `false` for every session. `null` means stable
 * IDs are disabled for this key; the user is then passed through unchanged.
 */
function withStableId<T extends object>(sdkKey: string, user: T): T {
  const stableID = StableID.get(sdkKey);
  return stableID === null ? user : { ...user, customIDs: { stableID } };
}

/**
 * Log fields for an unsuccessful Statsig update. `source` shows which values
 * stay in use: `Cache` keeps cached values, `NoValues` turns every gate off.
 */
function statsigFailureFields(details: StatsigUpdateDetails): { error: string; source: string } {
  return { error: details.error?.message.slice(0, 200) ?? 'none', source: details.source };
}

const STABLE_ID_GATE_NAMES: string[] = Object.values(featureGates)
  .filter((gate) => gate.idType === 'stableID')
  .map((gate) => gate.name);

/**
 * `true` when a settled payload has none of the registered stableID gates. One
 * gate can be missing on its own (enabled with no rules, or deleted); all of
 * them missing means Statsig evaluated without the stableID.
 */
function payloadHasNoStableIdGates(client: StatsigClient): boolean {
  return (
    STABLE_ID_GATE_NAMES.length > 0 &&
    STABLE_ID_GATE_NAMES.every((gateName) => {
      const reason = client.getFeatureGate(gateName, { disableExposureLog: true }).details.reason;
      // `Loading:` means a newer update emptied the store; it is not this payload.
      return reason.endsWith(':Unrecognized') && !reason.startsWith('Loading:');
    })
  );
}

async function startSessionReplayAfterLoad(client: StatsigClient): Promise<void> {
  await waitForWindowLoad();
  try {
    const { runStatsigSessionReplay } = await import('@statsig/session-replay');
    // @ts-expect-error REASON: @statsig types incompatible with exactOptionalPropertyTypes (library bug)
    runStatsigSessionReplay(client);
  } catch (error: unknown) {
    logger.warn('Statsig session replay initialization failed', {
      error: error instanceof Error ? error.message.slice(0, 200) : String(error),
    });
  }
}

function waitForWindowLoad(): Promise<void> {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

export function createStatsigProvider(): AnalyticsProvider {
  return new StatsigAnalyticsProvider();
}
